import { required as required_1 } from "../../../../../WidgetRegistrations/registrationWidgetManifest.schema.json";
import { Measures } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { requestRefresh } from "../../externalApi/DashboardHostApi";
import { getHostRegistrarApi } from "../../externalApi/RegistrarApi";
import { addSystemThemeChangedEventListener } from "../../externalApi/ThemeApi";
import { isFeatureEnabled } from "../../externalApi/VelocityApi";
import { deepCompare } from "../../utils/objectCompareUtilities";
import { getAttributionResourceNameFromID, getDisplayResourceNameFromID } from "./registrarHelpers";
import { createHash } from "crypto";
import { openDB } from "idb";
import log from "loglevel";
import { getResources } from "../../getResources";
import { lazyLoadModule } from "../../lazyLoadModule";
import { tryParseJson } from "../../utils/stringUtilities";
import { getWidgetOrchestrator } from "../orchestrator/orchestrator";
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../../../telemetry/Telemetry");
function isRegistrationCacheEnabled() {
    return isFeatureEnabled("Feature_CacheWidgetRegistrations");
}
// Note: These are just the required properties. There can be non-required properties loaded in. It's up to the caller to
// perform the correct checks/functionality in order to use those properties.
// TODO: figure out the correct way to handle loading in the displayName, icon and placeholderImage. They aren't actually strings
// at runtime. displayName is an array of dictionaries{locale, name}, and icon and placeholderImage are dictionaries{src}.
export class WidgetRegistration {
    constructor(displayName, providerDisplayName, entryPoint, icon, id, providerId, appId, isDefault, placeholderImage, backingAppPFN, entryPointOverrides, defaults, maxInstances, supportedAuth, supportedSizes, activationUrl, preferredSize, exclusiveRegions, excludedRegions, excludedRegionsByLocation, settingsAvailable, attributionString, isAdaptiveCard, isTombstoned) {
        Trace.logEvent(dashboard, "WebAppWidgetRegistrationCreated", 0 /* verbose */, {
            widgetDefinitionId: id,
        });
        if (defaults !== undefined) {
            this.defaults = defaults;
        }
        this.displayName = displayName;
        this.providerDisplayName = providerDisplayName;
        this.entryPoint = entryPoint;
        this.icon = icon;
        this.id = id;
        this.providerId = providerId;
        this.appId = appId;
        this.isDefault = isDefault;
        this.placeholderImage = placeholderImage;
        // Defaults for optional properties
        this.backingAppPFN = backingAppPFN !== null && backingAppPFN !== void 0 ? backingAppPFN : "";
        this.entryPointOverrides = entryPointOverrides;
        this.maxInstances = maxInstances !== null && maxInstances !== void 0 ? maxInstances : 0; // no max
        this.supportedAuth = supportedAuth !== null && supportedAuth !== void 0 ? supportedAuth : ["AAD", "MSA", "NONE"];
        this.supportedSizes = supportedSizes !== null && supportedSizes !== void 0 ? supportedSizes : ["large", "medium", "small"];
        this.activationUrl = activationUrl !== null && activationUrl !== void 0 ? activationUrl : null;
        this.preferredSize = preferredSize !== null && preferredSize !== void 0 ? preferredSize : "medium";
        this.exclusiveRegions = exclusiveRegions;
        this.excludedRegions = excludedRegions;
        this.excludedRegionsByLocation = excludedRegionsByLocation;
        this.settingsAvailable = settingsAvailable !== null && settingsAvailable !== void 0 ? settingsAvailable : false;
        this.attributionString = attributionString !== null && attributionString !== void 0 ? attributionString : "";
        this.isAdaptiveCard = isAdaptiveCard !== null && isAdaptiveCard !== void 0 ? isAdaptiveCard : (entryPoint.host !== undefined && entryPoint.type === "PFN");
        this.isTombstoned = isTombstoned !== null && isTombstoned !== void 0 ? isTombstoned : false;
        // In the interest of perf we don't load the display resource for logging
        log.info("=== Widget Registration [" + id + "] ===");
        log.info(id);
        log.info("activationUrl: " + this.activationUrl);
        log.info("preferredSize: " + this.preferredSize);
    }
}
// Constants for the registration cache
const registrationCacheDbPath = "com.microsoft.registrationCache";
const registrationStoreName = "widgetRegistrations";
class WidgetRegistrarImpl {
    constructor() {
        this.widgets = new Array();
        this.feeds = new Array();
        this.isSideLoaded = false;
        this.processManifest = new Promise(async (resolve, reject) => {
            // default app manifest
            await this.initWidgetRegistrations();
            resolve(undefined);
        });
        const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
        if (widgetModelApiFactory) {
            const packageStateApi = widgetModelApiFactory.tryGetApi("packagestate", 1, 1);
            if (packageStateApi) {
                packageStateApi.addEventListener(this.initWidgetRegistrations);
            }
        }
    }
    getWidgetRegistrations() {
        return new Promise(async (resolve, reject) => {
            await this.processManifest;
            resolve(this.widgets);
        });
    }
    getFeedRegistrations() {
        return new Promise((resolve, reject) => {
            resolve(this.feeds);
        });
    }
    async isTombstoned(registration) {
        const isFeatureDMAEdgeUninstallEnabled = isFeatureEnabled("Feature_DMAEdgeUninstall");
        if (isFeatureDMAEdgeUninstallEnabled) {
            const backingAppPFN = registration.backingAppPFN;
            if (backingAppPFN !== "") {
                const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
                const packageStateApi = widgetModelApiFactory.tryGetApi("packagestate", 1, 1);
                if (packageStateApi) {
                    return !(await packageStateApi.isPackageInstalled(backingAppPFN));
                }
            }
        }
        return false;
    }
    async initWidgetRegistrations() {
        Measures.logEvent(dashboard, "initWidgetRegistrationsBegin", 16777216 /* productAndServicePerformance */, {});
        if (isRegistrationCacheEnabled()) {
            await this.initializeRegistrationCacheDatabase();
            // Load cached registrations from indexdb
            await this.loadCachedHostRegistrarWidgetDefinitions();
            if (this.widgets.length == 0) {
                // If the cached registration list is empty, we must force them to be loaded now
                let hostRegistrarWidgetDefinitionIds = await this.loadHostRegistrarWidgetDefinitionIds();
                await this.loadHostRegistrarWidgetDefinitions(hostRegistrarWidgetDefinitionIds);
            }
            else {
                // TODO: Select an appropriate delay before attempting to update registrations
                // Wait 4 seconds and then start the async call
                // the list loaded from the cache above.
                setTimeout(() => {
                    this.startLoadHostRegistrarWidgetDefinitionsAsync();
                }, 1000 * 4);
            }
        }
        else {
            // Get a list of all the widget definitions it provides and
            // insert or replace into our registration list.
            // Call this last after all other registration loads have occured.
            let hostRegistrarWidgetDefinitionIds = await this.loadHostRegistrarWidgetDefinitionIds();
            await this.loadHostRegistrarWidgetDefinitions(hostRegistrarWidgetDefinitionIds);
        }
        // Log list of available widgets
        this.logAvailableWidgetsList();
    }
    logAvailableWidgetsList() {
        Measures.logEvent(dashboard, "AvailableWidgetsList", 16777216 /* productAndServicePerformance */, {
            widgetsList: this.widgets.map((widget) => ({
                appId: widget.appId,
                backingAppPFN: widget.backingAppPFN,
                isTombstoned: widget.isTombstoned,
                providerId: widget.providerId,
                providerName: widget.providerDisplayName,
                widgetDefinitionId: widget.id,
                widgetDisplayName: widget.displayName,
            })),
        });
    }
    async loadHostRegistrarWidgetDefinitionIds() {
        let hostRegistrarApi = await getHostRegistrarApi();
        if (!hostRegistrarApi) {
            return new Array();
        }
        else {
            // Return a promise that on resolve gives an array of the unique widget definition ids that the host provides.
            return new Promise(async (resolve) => {
                await (hostRegistrarApi === null || hostRegistrarApi === void 0 ? void 0 : hostRegistrarApi.getWidgetDefinitionIdsAsync((hresult, uniqueWidgetIds) => {
                    if (hresult !== 0) {
                        Measures.logEvent(dashboard, "GetWidgetDefinitionFailed", 16777216 /* productAndServicePerformance */, {
                            hresult: hresult,
                            reason: "HostRegistrar getWidgetDefinitionIdsAsync failed",
                        });
                        resolve([]);
                    }
                    resolve(uniqueWidgetIds);
                }));
            });
        }
    }
    async loadCachedHostRegistrarWidgetDefinitions() {
        // This should only be called once on cold start
        let db = await openDB(registrationCacheDbPath);
        db.onerror = function (event) {
            let eventReceived = event.target !== null ? event.target : null;
            let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
            Measures.logEvent(dashboard, "CachedRegistrationLoadFailed", 16777216 /* productAndServicePerformance */, {
                error: errorMessage,
            });
        };
        // If the database does not have a registration store just exit
        if (db.objectStoreNames.contains(registrationStoreName)) {
            let transaction = db.transaction(registrationStoreName, "readonly");
            transaction.onerror = () => {
                db.close();
                return new Error("widgetRegistrations store not found");
            };
            let store = transaction.objectStore(registrationStoreName);
            const cachedRegistrationStrings = await store.getAll();
            cachedRegistrationStrings.map((registration) => {
                this.widgets.push(JSON.parse(registration));
            });
        }
        ;
        db.close();
    }
    async deleteCachedRegistrationAsync(widgetDefinitionId) {
        return new Promise(async (resolve, reject) => {
            let db = await openDB(registrationCacheDbPath);
            db.onerror = function (event) {
                let eventReceived = event.target !== null ? event.target : null;
                let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
                Measures.logEvent(dashboard, "deleteCachedRegistrationAsyncFailed", 16777216 /* productAndServicePerformance */, {
                    error: errorMessage,
                });
            };
            // If the database does not have a registration store just exit
            if (db.objectStoreNames.contains(registrationStoreName)) {
                let transaction = db.transaction(registrationStoreName, "readwrite");
                transaction.onerror = () => {
                    db.close();
                    return new Error("widgetRegistrations store not found");
                };
                let store = transaction.objectStore(registrationStoreName);
                await store.delete(widgetDefinitionId);
            }
            ;
            db.close();
        });
    }
    // Open the database and ensure that the object store is created. Users of the database must
    // test for and await registrationCacheDataMigrationFinished before creating transactions
    async initializeRegistrationCacheDatabase() {
        const DBOpenRequest = window.indexedDB.open(registrationCacheDbPath, 2);
        DBOpenRequest.onupgradeneeded = (result) => {
            this.registrationCacheDataMigrationFinished = new Promise(async (resolveUpgrade) => {
                if (!DBOpenRequest.result.objectStoreNames.contains(registrationStoreName)) {
                    DBOpenRequest.result.createObjectStore(registrationStoreName);
                }
                resolveUpgrade();
            });
        };
    }
    async updateCachedRegistration(registration) {
        return new Promise(async (resolve, reject) => {
            // Wait for initialization or migration of the database if it is in progress
            if (this.registrationCacheDataMigrationFinished) {
                await this.registrationCacheDataMigrationFinished;
            }
            const DBOpenRequest = window.indexedDB.open(registrationCacheDbPath);
            // Create event handlers for both success and failure
            DBOpenRequest.onerror = function (event) {
                let eventReceived = event.target !== null ? event.target : null;
                let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
                Measures.logEvent(dashboard, "CachedRegistrationFailure", 16777216 /* productAndServicePerformance */, { context: "updateCachedRegistration", error: errorMessage });
                // If any error occurs, delete the database
                window.indexedDB.deleteDatabase(registrationCacheDbPath);
                reject(new Error("DbNotFound"));
            };
            DBOpenRequest.onsuccess = () => {
                let db = DBOpenRequest.result;
                // Open a transaction to the database
                let transaction = db.transaction(registrationStoreName, "readwrite");
                // Report on the success of the transaction completing, when everything is done
                // Hits after objectStoreRequest completion
                transaction.oncomplete = () => {
                    Measures.logEvent(dashboard, "CachedRegistrationSucceeded", 16777216 /* productAndServicePerformance */, {});
                    resolve();
                };
                transaction.onerror = function (event) {
                    let eventReceived = event.target !== null ? event.target : null;
                    let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
                    Measures.logEvent(dashboard, "CachedRegistrationFailure", 16777216 /* productAndServicePerformance */, {
                        error: errorMessage,
                    });
                    reject(new Error("Transaction not opened due to error."));
                };
                let store = transaction.objectStore(registrationStoreName);
                // Updates an entry if it already exists, otherwise creates a new one
                let objectStoreRequest = store.put(JSON.stringify(registration), registration.id);
                objectStoreRequest.onsuccess = () => {
                    // report the success of our request
                    // (to detect whether it has been successfully
                    // added to the database, you'd look at transaction.oncomplete)
                };
                objectStoreRequest.onerror = function (event) {
                    reject(new Error("WidgetNotFound"));
                };
                db.close();
            };
        });
    }
    // Start a promise to load registrations from the host in the 'background' do not await this method.
    async startLoadHostRegistrarWidgetDefinitionsAsync() {
        // Get a list of all the widget definitions the host provides
        let hostRegistrarWidgetDefinitionIds = await this.loadHostRegistrarWidgetDefinitionIds();
        Measures.logEvent(dashboard, "StartLoadHostRegistrarWidgetDefinitionsAsyncStarted", 16777216 /* productAndServicePerformance */, {
            registrationCount: hostRegistrarWidgetDefinitionIds.length,
        });
        let hostRegistrarApi = await getHostRegistrarApi();
        if (!hostRegistrarApi) {
            Measures.logEvent(dashboard, "StartLoadHostRegistrarWidgetDefinitionsAsyncFailed", 16777216 /* productAndServicePerformance */, {
                hresult: 0x80004001,
                reason: "MissingProviderAPI",
            });
        }
        else {
            // Check the existing this.widgets for registrations that are not in the hostRegistrarWidgetDefinitionIds list
            // indicating that they were uninstalled.
            // We want to wait on all of these to complete.
            // Copy the list of ids so we won't be reading the array we are modifying.
            let currentDefinitionIds = this.widgets.map((registration) => registration.id);
            await Promise.all(currentDefinitionIds.map(async (existingRegistrationId) => {
                // If the host does not have an id in this.widgets delete all instances and its registration
                if (hostRegistrarWidgetDefinitionIds.findIndex((id) => existingRegistrationId === id) === -1) {
                    await this.onPlatformWidgetRegistrationRemovedAsync(existingRegistrationId);
                }
            }));
            // Record any definitons that fail to load
            let failedWidgetDefinitions = [];
            // Return a promise for loading each widgetDefinitionId and adding it to the widgets list.
            // If any registration was added or removed, the UI will need to reload.
            var reloadUI = false;
            await Promise.all(hostRegistrarWidgetDefinitionIds.map(async (widgetDefinitionId) => {
                // Create a promise for getting the registration json string for a given widget.
                let getWidgetDefinitionPromise = new Promise(async (resolve) => {
                    await (hostRegistrarApi === null || hostRegistrarApi === void 0 ? void 0 : hostRegistrarApi.getWidgetDefinitionAsync(widgetDefinitionId, (hresult, widgetDefinitionJson) => {
                        if (hresult !== 0) {
                            failedWidgetDefinitions.push(widgetDefinitionId);
                            resolve("");
                        }
                        resolve(widgetDefinitionJson);
                    }));
                });
                let definitionString = await getWidgetDefinitionPromise;
                // Get existing registration to see if it already exists
                let registration = await this.getRegistration(widgetDefinitionId);
                // If this definiton was not previously known, cause the UI to reload
                reloadUI = registration === null;
                // Ensure that the in memory registration details are correct
                // This will also update the cache if the registration details have changed
                let registrationUpdated = await this.updateRegistration(definitionString);
                if (!reloadUI && registrationUpdated) {
                    registration = await this.getRegistration(widgetDefinitionId);
                    // If registration was updated reload refresh its instances.  No need to do this if the UI is going to reload anyway.
                    let orchestrator = await getWidgetOrchestrator();
                    let instances = await orchestrator.getWidgetInstances();
                    // Update every instance that has this registration
                    instances.forEach((widgetInstance) => {
                        if (widgetInstance.widgetDefinitionId == widgetDefinitionId) {
                            if (registration) {
                                widgetInstance.updateRegistration(registration).then(() => widgetInstance.refreshContent());
                            }
                        }
                    });
                }
            }));
            Measures.logEvent(dashboard, "StartLoadHostRegistrarWidgetDefinitionsAsyncFinished", 16777216 /* productAndServicePerformance */, {
                reloadUI: reloadUI,
                failedWidgetCount: failedWidgetDefinitions.length,
                failedWidgets: failedWidgetDefinitions.toString(),
            });
            if (reloadUI) {
                await requestRefresh();
            }
        }
    }
    // When caching is enabled, this is only called when the platform updates or adds a registration.
    // When caching is NOT enabled, this call is awaited on cold launch in initWidgetRegistrations
    async loadHostRegistrarWidgetDefinitions(hostRegistrarWidgetDefinitionIds) {
        Measures.logEvent(dashboard, "LoadHostRegistrarWidgetDefinitionsStarted", 16777216 /* productAndServicePerformance */, {
            countToAdd: hostRegistrarWidgetDefinitionIds.length,
        });
        let countAdded = 0;
        let hostRegistrarApi = await getHostRegistrarApi();
        if (!hostRegistrarApi) {
            Measures.logEvent(dashboard, "LoadHostRegistrarWidgetDefinitionsFailed", 16777216 /* productAndServicePerformance */, {
                hresult: 0x80004001,
                reason: "MissingProviderAPI",
            });
        }
        else {
            let failedWidgetDefinitions = [];
            // Return a promise for loading each widgetDefinitionId and adding it to the widgets list.
            await Promise.all(hostRegistrarWidgetDefinitionIds.map(async (widgetDefinitionId) => {
                // Create a promise for getting the registration json string for a given widget.
                let getWidgetDefinitionPromise = new Promise(async (resolve) => {
                    await (hostRegistrarApi === null || hostRegistrarApi === void 0 ? void 0 : hostRegistrarApi.getWidgetDefinitionAsync(widgetDefinitionId, (hresult, widgetDefinitionJson) => {
                        if (hresult !== 0) {
                            failedWidgetDefinitions.push(widgetDefinitionId);
                            resolve("");
                        }
                        countAdded++;
                        resolve(widgetDefinitionJson);
                    }));
                });
                let definitionString = await getWidgetDefinitionPromise;
                if (definitionString !== "") {
                    await this.updateRegistration(definitionString);
                }
            }));
            Measures.logEvent(dashboard, "LoadHostRegistrarWidgetDefinitionsComplete", 16777216 /* productAndServicePerformance */, {
                countAdded: countAdded,
                failedWidgets: failedWidgetDefinitions.toString(),
            });
        }
    }
    removeRegistration(widgetDefinitionId) {
        // Locate an existing registration with the same widgetDefinitionId and remove it
        let foundWidgetDefinitionIndex = this.widgets.findIndex((widgetRegistration) => widgetRegistration.id === widgetDefinitionId);
        if (foundWidgetDefinitionIndex != -1) {
            this.widgets.splice(foundWidgetDefinitionIndex, 1);
            Measures.logEvent(dashboard, "widgetDefinitionRemoved", 16777216 /* productAndServicePerformance */, {
                widgetDefinitionId: widgetDefinitionId,
            });
        }
        if (isRegistrationCacheEnabled()) {
            this.deleteCachedRegistrationAsync(widgetDefinitionId);
        }
    }
    async updateRegistration(definitionString) {
        let registrationUpdated = false;
        let widgetDefinition = this.parseWidgetDefinition(definitionString);
        if (widgetDefinition) {
            let isTombstoned = await this.isTombstoned(widgetDefinition);
            let widgetRegistration = this.loadWidgetRegistration(widgetDefinition, isTombstoned);
            // Specific break out for feed
            if (widgetRegistration.id === "com.msn.desktopfeed") {
                this.feeds = []; // empty the array, there can be only one
                this.feeds.push(widgetRegistration);
            }
            else {
                registrationUpdated = this.tryUpdateRegistrationInCache(widgetRegistration);
            }
        }
        return registrationUpdated;
    }
    parseWidgetDefinition(definitionString) {
        try {
            let json = JSON.parse(definitionString);
            // Copy every property from the json.
            let widgetDefinition = Object.assign({}, json);
            return widgetDefinition;
        }
        catch (ex) {
            Measures.logEvent(dashboard, "ParseWidgetRegistrationFailed", 16777216 /* productAndServicePerformance */, {
                exception: ex,
                reason: "Parsing widget registration failed or registrar manifest missing required property"
            });
        }
        return undefined;
    }
    tryUpdateRegistrationInCache(registration) {
        let registrationUpdated = false;
        let foundWidgetDefinitionIndex = this.widgets.findIndex((widgetDefinition) => widgetDefinition.id === registration.id);
        if (foundWidgetDefinitionIndex != -1) {
            if (isRegistrationCacheEnabled()) {
                let currentRegistration = this.widgets[foundWidgetDefinitionIndex];
                // If the registration is already known compare the new one with the old one
                // If the registration has changed, update the cache and return true.
                if (!deepCompare(currentRegistration, registration)) {
                    this.updateCachedRegistration(registration);
                    registrationUpdated = true;
                }
            }
        }
        else {
            if (isRegistrationCacheEnabled()) {
                this.updateCachedRegistration(registration);
            }
        }
        if (registrationUpdated) {
            this.widgets.splice(foundWidgetDefinitionIndex, 1);
            this.widgets.push(registration);
        }
        // If the registration wasn't alredy known, just add it.
        if (foundWidgetDefinitionIndex === -1) {
            this.widgets.push(registration);
        }
        return registrationUpdated;
    }
    loadWidgetRegistration(widgetDefinition, isTombstoned) {
        var _a, _b, _c, _d, _e;
        // Copy every property from the json.
        let widgetRegistration = undefined;
        // Only parse manifests here that are in the schema from registrationWidgetManifest.schema.json
        // WidgetDefinition JSON from the WidgetService will not have this schema
        if (widgetDefinition["$schema"] && widgetDefinition["$schema"].endsWith("registrationWidgetManifest.schema.json")) {
            // Verify that the required properties as defined in the schema are present.
            let mappedKeys = new Set(Object.keys(widgetDefinition));
            required_1.forEach((item) => {
                if (!mappedKeys.has(item)) {
                    throw new Error("Registrar manifest missing required property.");
                }
            });
            let displayNameResourceId = getDisplayResourceNameFromID(widgetDefinition.id);
            let displayName = getResources()[displayNameResourceId];
            if (displayName === undefined) {
                displayName = widgetDefinition.displayName;
            }
            let attributionNameResourceId = getAttributionResourceNameFromID(widgetDefinition.id);
            let attributionText = getResources()[attributionNameResourceId];
            if (attributionText === undefined) {
                attributionText = widgetDefinition.attributionString;
            }
            widgetRegistration = new WidgetRegistration(displayName, (_a = widgetDefinition.providerDisplayName) !== null && _a !== void 0 ? _a : displayName, // If no provider string, reuse the display name
            widgetDefinition.entryPoint, widgetDefinition.icon, widgetDefinition.id, (_b = widgetDefinition.providerId) !== null && _b !== void 0 ? _b : widgetDefinition.id, // Use the definiton id if no provider id
            (_c = widgetDefinition.appId) !== null && _c !== void 0 ? _c : widgetDefinition.id, // Use the definiton id if no app id
            false, widgetDefinition.placeholderImage, (_d = widgetDefinition.backingAppPFN) !== null && _d !== void 0 ? _d : "", widgetDefinition.entryPointOverrides, undefined, 1, // All legacy and pegergine widgets have max instances of 1
            widgetDefinition.supportedAuth, widgetDefinition.supportedSizes, widgetDefinition.activationUrl, widgetDefinition.preferredSize, widgetDefinition.exclusiveRegions, widgetDefinition.excludedRegions, widgetDefinition.excludedRegionsByLocation, widgetDefinition.settingsAvailable, attributionText, false, isTombstoned);
        }
        else {
            let entryPoint = {
                host: "",
                type: "PLATFORM",
                className: "",
                configuration: undefined,
            };
            let placeholderImages = this.getGenericPlaceholderImages();
            const allowMultiInstanceWidgets = isFeatureEnabled("Feature_AllowMultiInstanceWidgets");
            widgetRegistration = new WidgetRegistration(widgetDefinition.displayName, widgetDefinition.providerDisplayName, entryPoint, widgetDefinition.icon, widgetDefinition.Id, widgetDefinition.ProviderId, widgetDefinition.AppId, false, // isDefault pinned widget, TODO: http://task.ms/40365998 Need to find a new way to describe this or eliminate it.
            placeholderImages, (_e = widgetDefinition.backingAppPFN) !== null && _e !== void 0 ? _e : "", undefined, // widget.entryPointOverrides,
            undefined, 
            // TODO: http://task.ms/43321948 Revert this when we can re-enable multi instance behavior
            // For now force all registrations to be single instance
            allowMultiInstanceWidgets ? (widgetDefinition.maxInstances ? +widgetDefinition.maxInstances : 0) : 1, // Zero indicates no maximum
            undefined, // widget.supportedAuth 3rd party not needed
            widgetDefinition.supportedSizes, (widgetDefinition.activationUrl != "") ? widgetDefinition.activationUrl : undefined, widgetDefinition.preferredSize, undefined, // widget.exclusiveRegions, The widget platform will prefilter with its own logic.
            undefined, // widget.excludedRegions, The widget platform will prefilter with its own logic.
            undefined, // widget.excludedRegionsByLocation, The widget platform will prefilter with its own logic.
            widgetDefinition.settingsAvailable, undefined, // attributionString, TODO: http://task.ms/40366182 Add to platform definiton?
            true, //  isAdaptiveCard,  this is always true for the widgets platform
            isTombstoned);
        }
        if (!widgetRegistration) {
            throw new Error("Registrar manifest missing required property.");
        }
        return widgetRegistration;
    }
    getGenericPlaceholderImages() {
        let lightSmall = {
            theme: "light",
            size: "small",
            src: "./placeholder-3p/light-small.svg",
        };
        let lightMedium = {
            theme: "light",
            size: "medium",
            src: "./placeholder-3p/light-medium.svg",
        };
        let lightLarge = {
            theme: "light",
            size: "large",
            src: "./placeholder-3p/light-large.svg",
        };
        let darkSmall = {
            theme: "dark",
            size: "small",
            src: "./placeholder-3p/dark-small.svg",
        };
        let darkMedium = {
            theme: "dark",
            size: "medium",
            src: "./placeholder-3p/dark-medium.svg",
        };
        let darkLarge = {
            theme: "dark",
            size: "large",
            src: "./placeholder-3p/dark-large.svg",
        };
        return [lightLarge, lightMedium, lightSmall, darkLarge, darkMedium, darkSmall];
    }
    async getRegistration(widgetDefinitionId) {
        let registrations = await this.getWidgetRegistrations();
        for (let i = 0; i < registrations.length; i++) {
            if (registrations[i].id === widgetDefinitionId) {
                return registrations[i];
            }
        }
        return null;
    }
    onPlatformWidgetRegistrationUpdated(widgetDefinitionIdUpdated) {
        Trace.logEvent(dashboard, "onPlatformWidgetRegistrationUpdated", 0 /* verbose */, {
            widgetId: widgetDefinitionIdUpdated,
        });
        // Reload the single definition that was updated
        this.loadHostRegistrarWidgetDefinitions([widgetDefinitionIdUpdated]).then(async () => {
            // Refresh the frames and redraw all of the widget instances that have this definition id
            let orchestrator = await getWidgetOrchestrator();
            let instances = await orchestrator.getWidgetInstances();
            // Get the new registration.
            let registration = await this.getRegistration(widgetDefinitionIdUpdated);
            instances.forEach((widgetInstance) => {
                if (widgetInstance.widgetDefinitionId == widgetDefinitionIdUpdated) {
                    if (registration) {
                        widgetInstance.updateRegistration(registration).then(() => widgetInstance.refreshContent());
                    }
                }
            });
            let widgetModelApi = window
                .__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("model", 1, 1);
            // Notify WebXT
            widgetModelApi === null || widgetModelApi === void 0 ? void 0 : widgetModelApi.registrationsChanged([widgetDefinitionIdUpdated]);
        });
    }
    // Interpret theme change as all platform registrations have changed
    onThemeChanged() {
        this.loadHostRegistrarWidgetDefinitionIds().then(async (hostRegistrarWidgetDefinitionIds) => {
            let countUpdated = 0;
            try {
                lazyLoadModule(TelemetryModule).then((Telemetry) => {
                    Telemetry.logEvent(dashboard, "onThemeChangedRegistrationUpdate", 16777216 /* productAndServicePerformance */, {
                        countToUpdate: hostRegistrarWidgetDefinitionIds.length,
                    });
                });
                // Reloading each registration will update the affected widget instances
                hostRegistrarWidgetDefinitionIds.forEach((widgetDefinitionId) => {
                    this.onPlatformWidgetRegistrationUpdated(widgetDefinitionId);
                    countUpdated++;
                });
                lazyLoadModule(TelemetryModule).then((Telemetry) => {
                    Telemetry.logEvent(dashboard, "onThemeChangedRegistrationUpdateComplete", 16777216 /* productAndServicePerformance */, {
                        countUpdated: countUpdated,
                    });
                });
            }
            catch (ex) {
                lazyLoadModule(TelemetryModule).then((Telemetry) => {
                    Telemetry.logEvent(dashboard, "onThemeChangedRegistrationFailure", 16777216 /* productAndServicePerformance */, {
                        countUpdated: countUpdated,
                        exception: ex,
                    });
                });
            }
        });
    }
    // This call is only intended as a cold launch fix up for the condition that 3p widget provider apps have already
    // been installed prior to the host app that supports them being available or if they were somehow installed while
    // the host app was not running to receive the added events.
    async CatchUpNewWidgetRegistrationEvents() {
        Trace.logEvent(dashboard, "CatchUpNewWidgetRegistrationEvents", 0 /* verbose */, {});
        let newDefinitions = [];
        // Load the registrations already seen
        let knownRegistrationsSet = this.GetAlreadySeenRegistrations();
        // Enumerate all loaded registrationgs and notify WebXT for those not in the 'seen' list
        this.widgets.forEach((widgetRegistration) => {
            // Only for platform widgets
            if (widgetRegistration.entryPoint.type === "PLATFORM") {
                const widgetHash = this.GetRegistrationHash(widgetRegistration.id);
                if (!knownRegistrationsSet.has(widgetHash)) {
                    newDefinitions.push(widgetRegistration.id);
                    knownRegistrationsSet.add(widgetHash);
                }
            }
        });
        Trace.logEvent(dashboard, "CatchUpNewWidgetRegistrationEventsComplete", 0 /* verbose */, {
            newDefinitions: newDefinitions,
        });
        if (newDefinitions.length > 0) {
            // Notify WebXT
            let widgetModelApi = window
                .__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("model", 1, 1);
            widgetModelApi === null || widgetModelApi === void 0 ? void 0 : widgetModelApi.registrationsAdded(newDefinitions);
            // Save the known list
            window.localStorage.setItem("knownRegistrations", JSON.stringify([...knownRegistrationsSet]));
        }
    }
    GetRegistrationHash(widgetDefinitionId) {
        let hasher = createHash("sha256");
        return hasher.update(widgetDefinitionId).digest("base64");
    }
    GetAlreadySeenRegistrations() {
        const knownRegistrationsString = window.localStorage.getItem("knownRegistrations");
        if (knownRegistrationsString) {
            const registrationsJson = tryParseJson(knownRegistrationsString);
            if (registrationsJson) {
                return new Set(registrationsJson);
            }
        }
        return new Set();
    }
    IsAlreadySeenRegistration(widgetDefinitionId) {
        const knownRegistrationsSet = this.GetAlreadySeenRegistrations();
        const widgetHash = this.GetRegistrationHash(widgetDefinitionId);
        return knownRegistrationsSet.has(widgetHash);
    }
    AddAlreadySeenRegistration(widgetDefinitionId) {
        Trace.logEvent(dashboard, "AddAlreadySeenRegistration", 0 /* verbose */, {
            widgetDefinitionId: widgetDefinitionId,
        });
        let knownRegistrationsSet = this.GetAlreadySeenRegistrations();
        const widgetHash = this.GetRegistrationHash(widgetDefinitionId);
        knownRegistrationsSet.add(widgetHash);
        window.localStorage.setItem("knownRegistrations", JSON.stringify([...knownRegistrationsSet]));
    }
    RemoveAlreadySeenRegistration(widgetDefinitionId) {
        Trace.logEvent(dashboard, "RemoveAlreadySeenRegistration", 0 /* verbose */, {
            widgetDefinitionId: widgetDefinitionId,
        });
        let knownRegistrationsSet = this.GetAlreadySeenRegistrations();
        const widgetHash = this.GetRegistrationHash(widgetDefinitionId);
        if (knownRegistrationsSet.has(widgetHash)) {
            knownRegistrationsSet.delete(widgetHash);
            window.localStorage.setItem("knownRegistrations", JSON.stringify([...knownRegistrationsSet]));
        }
    }
    async onPlatformWidgetRegistrationAdded(widgetDefinitionId) {
        Trace.logEvent(dashboard, "onPlatformWidgetRegistrationAdded", 0 /* verbose */, {
            widgetDefinitionId: widgetDefinitionId,
        });
        let widgetRegistration = await this.getRegistration(widgetDefinitionId);
        if (!widgetRegistration) {
            await this.loadHostRegistrarWidgetDefinitions([widgetDefinitionId]);
            widgetRegistration = await this.getRegistration(widgetDefinitionId);
        }
        // Only record and notify new reigstrations for actual "PLATFORM" widgets
        // This will prevent the 'static' peregrine registrations that are now platform sourced
        // from appearing as new registrations.
        if (widgetRegistration &&
            widgetRegistration.entryPoint.type === "PLATFORM" &&
            !this.IsAlreadySeenRegistration(widgetDefinitionId)) {
            await this.loadHostRegistrarWidgetDefinitions([widgetDefinitionId]);
            // Notify WebXT
            let widgetModelApi = window
                .__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("model", 1, 1);
            widgetModelApi === null || widgetModelApi === void 0 ? void 0 : widgetModelApi.registrationsAdded([widgetDefinitionId]);
            // Add the registration to the already seen list.
            this.AddAlreadySeenRegistration(widgetDefinitionId);
        }
        Measures.logEvent(dashboard, "WidgetRegistrationAddedComplete", 16777216 /* productAndServicePerformance */, {
            appId: widgetRegistration === null || widgetRegistration === void 0 ? void 0 : widgetRegistration.appId,
            providerId: widgetRegistration === null || widgetRegistration === void 0 ? void 0 : widgetRegistration.providerId,
            providerName: widgetRegistration === null || widgetRegistration === void 0 ? void 0 : widgetRegistration.providerDisplayName,
            widgetDefinitionId: widgetRegistration === null || widgetRegistration === void 0 ? void 0 : widgetRegistration.id,
            widgetDisplayName: widgetRegistration === null || widgetRegistration === void 0 ? void 0 : widgetRegistration.displayName,
        });
    }
    async onPlatformWidgetRegistrationRemovedAsync(widgetDefinitionId) {
        Trace.logEvent(dashboard, "onPlatformWidgetRegistrationRemoved", 0 /* verbose */, {
            widgetDefinitionId: widgetDefinitionId,
        });
        let instancesRemoved = 0;
        let registrationRemoved = false;
        const widgetRegistration = await this.getRegistration(widgetDefinitionId);
        if (widgetRegistration && widgetRegistration.entryPoint.type === "PLATFORM") {
            // Notify WebXT
            let widgetModelApi = window
                .__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("model", 1, 1);
            widgetModelApi === null || widgetModelApi === void 0 ? void 0 : widgetModelApi.registrationsRemoved([widgetDefinitionId]);
            // Delete all instances with this definition id
            let orchestrator = await getWidgetOrchestrator();
            let instances = await orchestrator.getWidgetInstances();
            instances.forEach((widgetInstance) => {
                Trace.logEvent(dashboard, "widgetInstanceRemoved", 0 /* verbose */, {
                    widgetDefinitionId: widgetDefinitionId,
                    widgetInstanceId: widgetInstance.widgetId,
                });
                if (widgetInstance.widgetDefinitionId == widgetDefinitionId) {
                    orchestrator.removeWidgetInstance(widgetInstance);
                    instancesRemoved++;
                }
            });
            this.removeRegistration(widgetDefinitionId);
            registrationRemoved = true;
        }
        // Remove the registration from the already seen list.
        this.RemoveAlreadySeenRegistration(widgetDefinitionId);
        Measures.logEvent(dashboard, "WidgetRegistrationRemovedComplete", 16777216 /* productAndServicePerformance */, {
            instancesRemoved: instancesRemoved,
            registrationRemoved: registrationRemoved,
            widgetDefinitionId: widgetDefinitionId,
        });
    }
    async RegisterPlatformEventsAsync() {
        let hostRegistrarApi = await getHostRegistrarApi();
        if (hostRegistrarApi) {
            hostRegistrarApi.addEventListener("definitionUpdated", this.onPlatformWidgetRegistrationUpdated.bind(this));
            hostRegistrarApi.addEventListener("definitionAdded", this.onPlatformWidgetRegistrationAdded.bind(this));
            hostRegistrarApi.addEventListener("definitionRemoved", this.onPlatformWidgetRegistrationRemovedAsync.bind(this));
            let detectNewRegistrationsTimer = undefined;
            // 5 minutes after launch invoke CatchUpWidgetRegistrationEvents which will send notifications
            // of new widgets added by apps before this widgets version was installed or while not running.
            detectNewRegistrationsTimer = setTimeout(() => {
                this.CatchUpNewWidgetRegistrationEvents();
                if (detectNewRegistrationsTimer) {
                    clearTimeout(detectNewRegistrationsTimer);
                }
            }, 1000 * 60 * 5);
        }
        addSystemThemeChangedEventListener(this.onThemeChanged.bind(this));
    }
}
let g_registrarInstance;
export async function getWidgetRegistrarAsync() {
    if (!g_registrarInstance) {
        g_registrarInstance = new WidgetRegistrarImpl();
        await g_registrarInstance.RegisterPlatformEventsAsync();
    }
    return g_registrarInstance;
}
